export const BOARD_WIDTH = 10
export const BOARD_HEIGHT = 20

export type PieceType = 'I' | 'O' | 'T' | 'S' | 'Z' | 'J' | 'L'
export type Cell = PieceType | null
export type Board = Cell[][]

export interface ActivePiece {
  type: PieceType
  rotation: number
  x: number
  y: number
}

export interface LockResult {
  board: Board
  clearedLines: number
  power: PieceType
  powerName: string
  powerDescription: string
  powerColor: string
  powerVisual: PiecePower['visual']
  powerCells: Array<[number, number]>
  effectCells: Array<[number, number]>
  toppedOut: boolean
}

export interface PiecePower {
  name: string
  description: string
  color: string
  visual: 'beam' | 'burst' | 'ray' | 'slash-left' | 'slash-right' | 'drill' | 'lift'
}

export const piecePowers: Record<PieceType, PiecePower> = {
  I: { name: 'Beam Sweep', description: 'Sweeps up to four nearby blocks from the landing row.', color: '#5ee7ff', visual: 'beam' },
  O: { name: 'Nova Burst', description: 'Detonates a five-cell plus burst around the core.', color: '#ffd166', visual: 'burst' },
  T: { name: 'Prism Ray', description: 'Projects a three-cell ray upward from its crown.', color: '#c084fc', visual: 'ray' },
  S: { name: 'Phase Cut', description: 'Carves a three-cell slash to the left.', color: '#62e6a7', visual: 'slash-left' },
  Z: { name: 'Rift Cut', description: 'Carves a three-cell slash to the right.', color: '#ff718d', visual: 'slash-right' },
  J: { name: 'Anchor Drop', description: 'Drills through up to two blocks below its hook.', color: '#6f9cff', visual: 'drill' },
  L: { name: 'Solar Lift', description: 'Scorches up to two blocks above its foot.', color: '#ff9f5a', visual: 'lift' },
}

const SHAPES: Record<PieceType, ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>> = {
  I: [[[0, 0, 0, 0], [1, 1, 1, 1], [0, 0, 0, 0], [0, 0, 0, 0]], [[0, 0, 1, 0], [0, 0, 1, 0], [0, 0, 1, 0], [0, 0, 1, 0]]],
  O: [[[1, 1], [1, 1]]],
  T: [[[0, 1, 0], [1, 1, 1], [0, 0, 0]], [[0, 1, 0], [0, 1, 1], [0, 1, 0]], [[0, 0, 0], [1, 1, 1], [0, 1, 0]], [[0, 1, 0], [1, 1, 0], [0, 1, 0]]],
  S: [[[0, 1, 1], [1, 1, 0], [0, 0, 0]], [[0, 1, 0], [0, 1, 1], [0, 0, 1]]],
  Z: [[[1, 1, 0], [0, 1, 1], [0, 0, 0]], [[0, 0, 1], [0, 1, 1], [0, 1, 0]]],
  J: [[[1, 0, 0], [1, 1, 1], [0, 0, 0]], [[0, 1, 1], [0, 1, 0], [0, 1, 0]], [[0, 0, 0], [1, 1, 1], [0, 0, 1]], [[0, 1, 0], [0, 1, 0], [1, 1, 0]]],
  L: [[[0, 0, 1], [1, 1, 1], [0, 0, 0]], [[0, 1, 0], [0, 1, 0], [0, 1, 1]], [[0, 0, 0], [1, 1, 1], [1, 0, 0]], [[1, 1, 0], [0, 1, 0], [0, 1, 0]]],
}

export function createBoard(): Board {
  return Array.from({ length: BOARD_HEIGHT }, () => Array<Cell>(BOARD_WIDTH).fill(null))
}

export function cloneBoard(board: Board): Board {
  return board.map((row) => [...row])
}

export function cellsFor(piece: ActivePiece): Array<[number, number]> {
  const shape = SHAPES[piece.type][piece.rotation % SHAPES[piece.type].length]!
  const cells: Array<[number, number]> = []
  shape.forEach((row, dy) => row.forEach((filled, dx) => {
    if (filled) cells.push([piece.x + dx, piece.y + dy])
  }))
  return cells
}

export function canPlace(board: Board, piece: ActivePiece): boolean {
  return cellsFor(piece).every(([x, y]) => x >= 0 && x < BOARD_WIDTH && y < BOARD_HEIGHT && (y < 0 || board[y]![x] === null))
}

export function move(board: Board, piece: ActivePiece, dx: number, dy: number): ActivePiece | null {
  const next = { ...piece, x: piece.x + dx, y: piece.y + dy }
  return canPlace(board, next) ? next : null
}

export function rotate(board: Board, piece: ActivePiece, direction: 1 | -1 = 1): ActivePiece | null {
  const rotations = SHAPES[piece.type].length
  const next = { ...piece, rotation: (piece.rotation + direction + rotations) % rotations }
  for (const kick of [0, -1, 1, -2, 2]) {
    const kicked = { ...next, x: next.x + kick }
    if (canPlace(board, kicked)) return kicked
  }
  return null
}

export function hardDrop(board: Board, piece: ActivePiece): ActivePiece {
  let landed = piece
  while (true) {
    const next = move(board, landed, 0, 1)
    if (!next) return landed
    landed = next
  }
}

export function calculateGhostPiece(board: Board, activePiece: ActivePiece): ActivePiece {
  if (!canPlace(board, activePiece)) {
    return { ...activePiece }
  }
  let landed = activePiece
  while (true) {
    const next = move(board, landed, 0, 1)
    if (!next) return { ...landed }
    landed = next
  }
}

export const getGhostPiece = calculateGhostPiece

export function getGhostPieceCells(board: Board, activePiece: ActivePiece): Array<[number, number]> {
  return cellsFor(calculateGhostPiece(board, activePiece))
}

function addEffectCell(board: Board, cells: Array<[number, number]>, x: number, y: number): void {
  if (x < 0 || x >= BOARD_WIDTH || y < 0 || y >= BOARD_HEIGHT || board[y]![x] === null) return
  board[y]![x] = null
  cells.push([x, y])
}

function applyPower(board: Board, piece: ActivePiece, completedRows: number[] = []): Array<[number, number]> {
  const locked = new Set(cellsFor(piece).map(([x, y]) => `${x}:${y}`))
  const effects: Array<[number, number]> = []
  const cells = cellsFor(piece).filter(([x, y]) => x >= 0 && y >= 0 && x < BOARD_WIDTH && y < BOARD_HEIGHT)
  if (cells.length === 0) return effects

  if (piece.type === 'I' && completedRows.length === 0) {
    const row = Math.max(...cells.map(([, y]) => y))
    const center = Math.round(cells.reduce((sum, [x]) => sum + x, 0) / cells.length)
    const targets = Array.from({ length: BOARD_WIDTH }, (_, x) => x)
      .filter((x) => board[row]?.[x] !== null && !locked.has(`${x}:${row}`))
      .sort((a, b) => Math.abs(a - center) - Math.abs(b - center))
    for (const x of targets.slice(0, 4)) addEffectCell(board, effects, x, row)
  } else if (piece.type === 'O') {
    const cx = Math.round(cells.reduce((sum, [x]) => sum + x, 0) / cells.length)
    const cy = Math.round(cells.reduce((sum, [, y]) => sum + y, 0) / cells.length)
    for (const [x, y] of [[cx, cy], [cx - 1, cy], [cx + 1, cy], [cx, cy - 1], [cx, cy + 1]] as Array<[number, number]>) {
      if (!locked.has(`${x}:${y}`)) addEffectCell(board, effects, x, y)
    }
  } else if (piece.type === 'T') {
    const [x, y] = cells.reduce((best, cell) => cell[1] < best[1] ? cell : best, cells[0]!)
    for (let distance = 1; distance <= 3; distance++) addEffectCell(board, effects, x, y - distance)
  } else if (piece.type === 'S' || piece.type === 'Z') {
    const [x, y] = piece.type === 'S' ? cells.reduce((best, cell) => cell[0] < best[0] ? cell : best, cells[0]!) : cells.reduce((best, cell) => cell[0] > best[0] ? cell : best, cells[0]!)
    const direction = piece.type === 'S' ? -1 : 1
    for (let distance = 1; distance <= 3; distance++) addEffectCell(board, effects, x + direction * distance, y)
  } else {
    const [x, y] = piece.type === 'J' ? cells.reduce((best, cell) => cell[1] > best[1] ? cell : best, cells[0]!) : cells.reduce((best, cell) => cell[1] < best[1] ? cell : best, cells[0]!)
    const direction = piece.type === 'J' ? 1 : -1
    for (let distance = 1; distance <= 2; distance++) addEffectCell(board, effects, x, y + direction * distance)
  }
  return effects
}

export function lockPiece(board: Board, piece: ActivePiece): LockResult {
  const nextBoard = cloneBoard(board)
  const lockedCells = cellsFor(piece)
  const toppedOut = lockedCells.some(([x, y]) => y < 0 || x < 0 || x >= BOARD_WIDTH || nextBoard[y]?.[x] !== null)
  lockedCells.forEach(([x, y]) => { if (y >= 0 && y < BOARD_HEIGHT) nextBoard[y]![x] = piece.type })
  const completeRows = nextBoard.reduce<number[]>((rows, row, index) => {
    if (row.every((cell) => cell !== null)) rows.push(index)
    return rows
  }, [])
  const effectCells = applyPower(nextBoard, piece, completeRows)
  const cleared = nextBoard.filter((_, index) => !completeRows.includes(index))
  while (cleared.length < BOARD_HEIGHT) cleared.unshift(Array<Cell>(BOARD_WIDTH).fill(null))
  const power = piecePowers[piece.type]
  return { board: cleared, clearedLines: completeRows.length, power: piece.type, powerName: power.name, powerDescription: power.description, powerColor: power.color, powerVisual: power.visual, powerCells: lockedCells, effectCells, toppedOut }
}

export function spawn(type: PieceType): ActivePiece {
  return { type, rotation: 0, x: Math.floor((BOARD_WIDTH - 4) / 2), y: -1 }
}

export const pieceTypes: readonly PieceType[] = ['I', 'O', 'T', 'S', 'Z', 'J', 'L']

export function generateBag(randomFn: () => number = Math.random): PieceType[] {
  const bag: PieceType[] = [...pieceTypes]
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(randomFn() * (i + 1))
    const temp = bag[i]!
    bag[i] = bag[j]!
    bag[j] = temp
  }
  return bag
}

export function createNextQueue(minCapacity?: number, randomFn?: () => number): PieceType[]
export function createNextQueue(queue: PieceType[], minCapacity?: number, randomFn?: () => number): PieceType[]
export function createNextQueue(randomFn: () => number): PieceType[]
export function createNextQueue(
  first?: PieceType[] | number | (() => number),
  second?: number | (() => number),
  third?: () => number
): PieceType[] {
  let queue: PieceType[] = []
  let minCapacity = 7
  let randomFn: () => number = Math.random

  if (Array.isArray(first)) {
    queue = [...first]
    if (typeof second === 'number') {
      minCapacity = second
      if (typeof third === 'function') {
        randomFn = third
      }
    } else if (typeof second === 'function') {
      randomFn = second
    }
  } else if (typeof first === 'number') {
    minCapacity = first
    if (typeof second === 'function') {
      randomFn = second
    }
  } else if (typeof first === 'function') {
    randomFn = first
  }

  while (queue.length < minCapacity) {
    queue.push(...generateBag(randomFn))
  }

  return queue
}

export function pullFromQueue(
  queue: PieceType[],
  minCapacity: number = 7,
  randomFn: () => number = Math.random
): { piece: PieceType; queue: PieceType[] } {
  const current = queue.length === 0 ? createNextQueue(minCapacity, randomFn) : [...queue]
  const piece = current.shift()!
  const nextQueue = createNextQueue(current, minCapacity, randomFn)
  return { piece, queue: nextQueue }
}

export interface HoldState {
  heldPiece: PieceType | null
  canHold: boolean
}

export interface HoldPieceInput {
  activePiece: ActivePiece
  heldPiece: PieceType | null
  canHold: boolean
  queue: PieceType[]
  minCapacity?: number
  randomFn?: () => number
}

export interface HoldPieceResult {
  activePiece: ActivePiece
  heldPiece: PieceType | null
  canHold: boolean
  queue: PieceType[]
  swapped: boolean
}

export function createHoldState(initialPiece: PieceType | null = null): HoldState {
  return {
    heldPiece: initialPiece,
    canHold: true,
  }
}

export function resetHold(heldPiece: PieceType | null = null): HoldState {
  return {
    heldPiece,
    canHold: true,
  }
}

export function holdPiece(input: HoldPieceInput): HoldPieceResult
export function holdPiece(
  activePiece: ActivePiece,
  holdState: HoldState,
  queue: PieceType[],
  minCapacity?: number,
  randomFn?: () => number
): HoldPieceResult
export function holdPiece(
  activePiece: ActivePiece,
  heldPiece: PieceType | null,
  canHold: boolean,
  queue: PieceType[],
  minCapacity?: number,
  randomFn?: () => number
): HoldPieceResult
export function holdPiece(
  first: ActivePiece | HoldPieceInput,
  second?: HoldState | PieceType | null,
  third?: boolean | PieceType[],
  fourth?: PieceType[] | number,
  fifth?: number | (() => number),
  sixth?: () => number
): HoldPieceResult {
  let activePiece: ActivePiece
  let heldPiece: PieceType | null = null
  let canHold = true
  let queue: PieceType[] = []
  let minCapacity = 7
  let randomFn: () => number = Math.random

  if ('activePiece' in first) {
    activePiece = first.activePiece
    heldPiece = first.heldPiece
    canHold = first.canHold
    queue = first.queue
    if (typeof first.minCapacity === 'number') minCapacity = first.minCapacity
    if (typeof first.randomFn === 'function') randomFn = first.randomFn
  } else if (second !== null && typeof second === 'object' && 'canHold' in second) {
    activePiece = first
    heldPiece = second.heldPiece
    canHold = second.canHold
    queue = Array.isArray(third) ? third : []
    if (typeof fourth === 'number') minCapacity = fourth
    if (typeof fifth === 'function') randomFn = fifth
  } else {
    activePiece = first
    heldPiece = (second as PieceType | null) ?? null
    canHold = typeof third === 'boolean' ? third : true
    queue = Array.isArray(fourth) ? fourth : []
    if (typeof fifth === 'number') minCapacity = fifth
    if (typeof sixth === 'function') randomFn = sixth
  }

  // Swap restrictions: only one swap permitted per drop cycle before reset on lock
  if (!canHold) {
    return {
      activePiece,
      heldPiece,
      canHold: false,
      queue: [...queue],
      swapped: false,
    }
  }

  // If hold slot is empty, hold current active piece and draw next piece from the queue
  if (heldPiece === null) {
    const { piece: nextPiece, queue: nextQueue } = pullFromQueue(queue, minCapacity, randomFn)
    return {
      activePiece: spawn(nextPiece),
      heldPiece: activePiece.type,
      canHold: false,
      queue: nextQueue,
      swapped: true,
    }
  }

  // Swap current active piece with held piece
  const newActivePiece = spawn(heldPiece)
  return {
    activePiece: newActivePiece,
    heldPiece: activePiece.type,
    canHold: false,
    queue: [...queue],
    swapped: true,
  }
}

export const LINE_CLEAR_BASE_POINTS: Record<number, number> = {
  0: 0,
  1: 100,
  2: 300,
  3: 500,
  4: 800,
}

export const BACK_TO_BACK_MULTIPLIER = 1.5
export const COMBO_BASE_POINTS = 50
export const POWER_CELL_BASE_POINTS = 25
export const SOFT_DROP_POINT_PER_CELL = 1
export const HARD_DROP_POINT_PER_CELL = 2
export const DEFAULT_INITIAL_DROP_INTERVAL = 1000
export const MIN_DROP_INTERVAL = 50

export interface ScoreInput {
  linesCleared: number
  level?: number
  combo?: number
  backToBack?: boolean
  effectCellsCount?: number
  softDropDistance?: number
  hardDropDistance?: number
}

export interface ScoreResult {
  points: number
  score: number
  linePoints: number
  comboBonus: number
  backToBackBonus: number
  powerBonus: number
  softDropPoints: number
  hardDropPoints: number
  nextCombo: number
  nextBackToBack: boolean
  isBackToBack: boolean
}

export function calculateLevel(linesCleared: number, startLevel: number = 1): number {
  return Math.max(1, Math.floor(startLevel) + Math.floor(Math.max(0, linesCleared) / 10))
}

export function getDropInterval(level: number, minInterval: number = MIN_DROP_INTERVAL): number {
  const safeLevel = Math.max(1, Math.floor(level))
  if (safeLevel === 1) return DEFAULT_INITIAL_DROP_INTERVAL
  const factor = Math.max(0.1, 0.8 - (safeLevel - 1) * 0.007)
  const interval = Math.round(Math.pow(factor, safeLevel - 1) * 1000)
  return Math.max(minInterval, interval)
}

export function calculateLinePoints(linesCleared: number, level: number = 1): number {
  const safeLevel = Math.max(1, Math.floor(level))
  const safeLines = Math.max(0, Math.floor(linesCleared))
  const base = LINE_CLEAR_BASE_POINTS[safeLines] ?? (safeLines >= 4 ? 800 + (safeLines - 4) * 300 : 0)
  return base * safeLevel
}

export function calculateComboBonus(combo: number, level: number = 1): number {
  const safeLevel = Math.max(1, Math.floor(level))
  const safeCombo = Math.max(0, Math.floor(combo))
  return safeCombo * COMBO_BASE_POINTS * safeLevel
}

export function calculatePowerBonus(effectCellsCount: number, level: number = 1): number {
  const safeLevel = Math.max(1, Math.floor(level))
  const safeCells = Math.max(0, Math.floor(effectCellsCount))
  return safeCells * POWER_CELL_BASE_POINTS * safeLevel
}

export function calculateDropPoints(softDropDistance: number = 0, hardDropDistance: number = 0): number {
  const soft = Math.max(0, Math.floor(softDropDistance)) * SOFT_DROP_POINT_PER_CELL
  const hard = Math.max(0, Math.floor(hardDropDistance)) * HARD_DROP_POINT_PER_CELL
  return soft + hard
}

export function calculateScore(input: ScoreInput): ScoreResult
export function calculateScore(
  linesCleared: number,
  level?: number,
  combo?: number,
  backToBack?: boolean,
  effectCellsCount?: number,
  softDropDistance?: number,
  hardDropDistance?: number
): ScoreResult
export function calculateScore(
  first: number | ScoreInput,
  second?: number,
  third?: number,
  fourth?: boolean,
  fifth?: number,
  sixth?: number,
  seventh?: number
): ScoreResult {
  let linesCleared = 0
  let level = 1
  let combo = 0
  let backToBack = false
  let effectCellsCount = 0
  let softDropDistance = 0
  let hardDropDistance = 0

  if (typeof first === 'object' && first !== null) {
    linesCleared = first.linesCleared ?? 0
    level = first.level ?? 1
    combo = first.combo ?? 0
    backToBack = first.backToBack ?? false
    effectCellsCount = first.effectCellsCount ?? 0
    softDropDistance = first.softDropDistance ?? 0
    hardDropDistance = first.hardDropDistance ?? 0
  } else {
    linesCleared = typeof first === 'number' ? first : 0
    level = second ?? 1
    combo = third ?? 0
    backToBack = fourth ?? false
    effectCellsCount = fifth ?? 0
    softDropDistance = sixth ?? 0
    hardDropDistance = seventh ?? 0
  }

  const safeLevel = Math.max(1, Math.floor(level))
  const safeLines = Math.max(0, Math.floor(linesCleared))
  const safeCombo = Math.max(0, Math.floor(combo))

  const linePoints = calculateLinePoints(safeLines, safeLevel)

  let backToBackBonus = 0
  let nextBackToBack = backToBack

  if (safeLines >= 4) {
    if (backToBack) {
      backToBackBonus = Math.floor(linePoints * (BACK_TO_BACK_MULTIPLIER - 1))
    }
    nextBackToBack = true
  } else if (safeLines > 0) {
    nextBackToBack = false
  }

  let comboBonus = 0
  let nextCombo = 0

  if (safeLines > 0) {
    nextCombo = safeCombo + 1
    if (safeCombo > 0) {
      comboBonus = calculateComboBonus(safeCombo, safeLevel)
    }
  } else {
    nextCombo = 0
  }

  const powerBonus = calculatePowerBonus(effectCellsCount, safeLevel)
  const softDropPoints = Math.max(0, Math.floor(softDropDistance)) * SOFT_DROP_POINT_PER_CELL
  const hardDropPoints = Math.max(0, Math.floor(hardDropDistance)) * HARD_DROP_POINT_PER_CELL

  const points = linePoints + backToBackBonus + comboBonus + powerBonus + softDropPoints + hardDropPoints

  return {
    points,
    score: points,
    linePoints,
    comboBonus,
    backToBackBonus,
    powerBonus,
    softDropPoints,
    hardDropPoints,
    nextCombo,
    nextBackToBack,
    isBackToBack: nextBackToBack,
  }
}

export type GameMode = 'endless' | 'sprint' | 'power-run'
export type GameStatus = 'idle' | 'playing' | 'paused' | 'game-over' | 'won'

export const SPRINT_TARGET_LINES = 40
export const POWER_RUN_TARGET_SCORE = 25000
export const HIGH_SCORE_STORAGE_KEY = 'tetris_high_score'

export function loadHighScore(mode?: GameMode): number {
  if (typeof window === 'undefined' || !window.localStorage) return 0
  try {
    const key = mode ? `${HIGH_SCORE_STORAGE_KEY}_${mode}` : HIGH_SCORE_STORAGE_KEY
    const raw = window.localStorage.getItem(key)
    const val = raw ? parseInt(raw, 10) : 0
    return Number.isFinite(val) ? val : 0
  } catch {
    return 0
  }
}

export function saveHighScore(score: number, mode?: GameMode): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    const key = mode ? `${HIGH_SCORE_STORAGE_KEY}_${mode}` : HIGH_SCORE_STORAGE_KEY
    const current = loadHighScore(mode)
    if (score > current) {
      window.localStorage.setItem(key, String(score))
    }
  } catch {
    // Ignore storage quota or security errors
  }
}

export interface GameStats {
  piecesPlaced: number
  linesCleared: number
  powerActivations: number
  powerCellsCleared: number
  maxCombo: number
  singles: number
  doubles: number
  triples: number
  tetrises: number
}

export interface GameState {
  mode: GameMode
  status: GameStatus
  board: Board
  activePiece: ActivePiece | null
  ghostPiece: ActivePiece | null
  queue: PieceType[]
  heldPiece: PieceType | null
  canHold: boolean
  score: number
  highScore: number
  lines: number
  linesCleared: number
  level: number
  combo: number
  backToBack: boolean
  dropInterval: number
  lastLockResult: LockResult | null
  lastScoreResult: ScoreResult | null
  stats: GameStats
  elapsedMs: number
  targetGoal: number | null
  startLevel: number
  minCapacity: number
}

export type GameAction =
  | { type: 'START'; mode?: GameMode }
  | { type: 'PAUSE' }
  | { type: 'RESUME' }
  | { type: 'TOGGLE_PAUSE' }
  | { type: 'RESTART'; mode?: GameMode }
  | { type: 'RESET'; mode?: GameMode }
  | { type: 'MOVE_LEFT' }
  | { type: 'MOVE_RIGHT' }
  | { type: 'MOVE'; dx: number; dy?: number }
  | { type: 'ROTATE'; direction?: 1 | -1 }
  | { type: 'ROTATE_CW' }
  | { type: 'ROTATE_CCW' }
  | { type: 'SOFT_DROP' }
  | { type: 'HARD_DROP' }
  | { type: 'HOLD' }
  | { type: 'TICK'; deltaMs?: number }
  | { type: 'LOCK' }
  | { type: 'SET_HIGH_SCORE'; highScore: number }

export interface TetrisGameOptions {
  mode?: GameMode
  startLevel?: number
  initialHighScore?: number
  minCapacity?: number
  randomFn?: () => number
  autoStart?: boolean
  targetGoal?: number | null
}

export function createInitialGameState(options: TetrisGameOptions | GameMode = {}): GameState {
  const opts: TetrisGameOptions = typeof options === 'string' ? { mode: options } : options
  const mode = opts.mode ?? 'endless'
  const startLevel = opts.startLevel ?? 1
  const minCapacity = opts.minCapacity ?? 7
  const randomFn = opts.randomFn ?? Math.random
  const autoStart = opts.autoStart ?? true
  const highScore = opts.initialHighScore ?? loadHighScore(mode)

  const board = createBoard()
  let queue = createNextQueue(minCapacity, randomFn)

  let activePiece: ActivePiece | null = null
  let ghostPiece: ActivePiece | null = null
  const status: GameStatus = autoStart ? 'playing' : 'idle'

  if (autoStart) {
    const drawn = pullFromQueue(queue, minCapacity, randomFn)
    activePiece = spawn(drawn.piece)
    queue = drawn.queue
    ghostPiece = calculateGhostPiece(board, activePiece)
  }

  const targetGoal = opts.targetGoal !== undefined
    ? opts.targetGoal
    : (mode === 'sprint' ? SPRINT_TARGET_LINES : mode === 'power-run' ? POWER_RUN_TARGET_SCORE : null)

  return {
    mode,
    status,
    board,
    activePiece,
    ghostPiece,
    queue,
    heldPiece: null,
    canHold: true,
    score: 0,
    highScore,
    lines: 0,
    linesCleared: 0,
    level: startLevel,
    combo: 0,
    backToBack: false,
    dropInterval: getDropInterval(startLevel),
    lastLockResult: null,
    lastScoreResult: null,
    stats: {
      piecesPlaced: 0,
      linesCleared: 0,
      powerActivations: 0,
      powerCellsCleared: 0,
      maxCombo: 0,
      singles: 0,
      doubles: 0,
      triples: 0,
      tetrises: 0,
    },
    elapsedMs: 0,
    targetGoal,
    startLevel,
    minCapacity,
  }
}

function lockActivePiece(
  state: GameState,
  pieceToLock: ActivePiece,
  hardDropDistance: number,
  softDropDistance: number,
  randomFn: () => number,
  minCapacity: number
): GameState {
  const lockResult = lockPiece(state.board, pieceToLock)

  const scoreResult = calculateScore({
    linesCleared: lockResult.clearedLines,
    level: state.level,
    combo: state.combo,
    backToBack: state.backToBack,
    effectCellsCount: lockResult.effectCells.length,
    softDropDistance,
    hardDropDistance,
  })

  const nextScore = state.score + scoreResult.points
  const nextLines = state.lines + lockResult.clearedLines
  const nextLevel = calculateLevel(nextLines, state.startLevel)
  const nextDropInterval = getDropInterval(nextLevel)
  const nextHighScore = Math.max(state.highScore, nextScore)

  if (nextHighScore > state.highScore) {
    saveHighScore(nextHighScore, state.mode)
  }

  const nextStats: GameStats = {
    piecesPlaced: state.stats.piecesPlaced + 1,
    linesCleared: state.stats.linesCleared + lockResult.clearedLines,
    powerActivations: state.stats.powerActivations + (lockResult.effectCells.length > 0 ? 1 : 0),
    powerCellsCleared: state.stats.powerCellsCleared + lockResult.effectCells.length,
    maxCombo: Math.max(state.stats.maxCombo, scoreResult.nextCombo),
    singles: state.stats.singles + (lockResult.clearedLines === 1 ? 1 : 0),
    doubles: state.stats.doubles + (lockResult.clearedLines === 2 ? 1 : 0),
    triples: state.stats.triples + (lockResult.clearedLines === 3 ? 1 : 0),
    tetrises: state.stats.tetrises + (lockResult.clearedLines >= 4 ? 1 : 0),
  }

  const target = state.targetGoal
  let isWon = false
  if (state.mode === 'sprint' && target !== null && nextLines >= target) {
    isWon = true
  } else if (state.mode === 'power-run' && target !== null && nextScore >= target) {
    isWon = true
  }

  if (isWon) {
    return {
      ...state,
      status: 'won',
      board: lockResult.board,
      activePiece: null,
      ghostPiece: null,
      score: nextScore,
      highScore: nextHighScore,
      lines: nextLines,
      linesCleared: nextLines,
      level: nextLevel,
      combo: scoreResult.nextCombo,
      backToBack: scoreResult.nextBackToBack,
      dropInterval: nextDropInterval,
      lastLockResult: lockResult,
      lastScoreResult: scoreResult,
      stats: nextStats,
      canHold: true,
    }
  }

  const { piece: nextType, queue: nextQueue } = pullFromQueue(state.queue, minCapacity, randomFn)
  const nextActivePiece = spawn(nextType)

  const isTopOut = lockResult.toppedOut || !canPlace(lockResult.board, nextActivePiece)

  if (isTopOut) {
    return {
      ...state,
      status: 'game-over',
      board: lockResult.board,
      activePiece: null,
      ghostPiece: null,
      queue: nextQueue,
      score: nextScore,
      highScore: nextHighScore,
      lines: nextLines,
      linesCleared: nextLines,
      level: nextLevel,
      combo: scoreResult.nextCombo,
      backToBack: scoreResult.nextBackToBack,
      dropInterval: nextDropInterval,
      lastLockResult: lockResult,
      lastScoreResult: scoreResult,
      stats: nextStats,
      canHold: true,
    }
  }

  return {
    ...state,
    status: 'playing',
    board: lockResult.board,
    activePiece: nextActivePiece,
    ghostPiece: calculateGhostPiece(lockResult.board, nextActivePiece),
    queue: nextQueue,
    score: nextScore,
    highScore: nextHighScore,
    lines: nextLines,
    linesCleared: nextLines,
    level: nextLevel,
    combo: scoreResult.nextCombo,
    backToBack: scoreResult.nextBackToBack,
    dropInterval: nextDropInterval,
    lastLockResult: lockResult,
    lastScoreResult: scoreResult,
    stats: nextStats,
    canHold: true,
  }
}

export function createTetrisGameReducer(options: TetrisGameOptions = {}) {
  const randomFn = options.randomFn ?? Math.random
  const minCapacity = options.minCapacity ?? 7

  return function tetrisGameReducer(
    state: GameState = createInitialGameState(options),
    action: GameAction
  ): GameState {
    switch (action.type) {
      case 'START': {
        if (state.status === 'idle' || state.status === 'game-over' || state.status === 'won') {
          return createInitialGameState({
            ...options,
            mode: action.mode ?? state.mode,
            initialHighScore: state.highScore,
            autoStart: true,
          })
        }
        if (action.mode && action.mode !== state.mode) {
          return createInitialGameState({
            ...options,
            mode: action.mode,
            initialHighScore: state.highScore,
            autoStart: true,
          })
        }
        return state
      }

      case 'PAUSE': {
        if (state.status === 'playing') {
          return { ...state, status: 'paused' }
        }
        return state
      }

      case 'RESUME': {
        if (state.status === 'paused') {
          return { ...state, status: 'playing' }
        }
        return state
      }

      case 'TOGGLE_PAUSE': {
        if (state.status === 'playing') {
          return { ...state, status: 'paused' }
        }
        if (state.status === 'paused') {
          return { ...state, status: 'playing' }
        }
        return state
      }

      case 'RESTART':
      case 'RESET': {
        return createInitialGameState({
          ...options,
          mode: action.mode ?? state.mode,
          initialHighScore: state.highScore,
          autoStart: true,
        })
      }

      case 'MOVE_LEFT': {
        if (state.status !== 'playing' || !state.activePiece) return state
        const moved = move(state.board, state.activePiece, -1, 0)
        if (!moved) return state
        return {
          ...state,
          activePiece: moved,
          ghostPiece: calculateGhostPiece(state.board, moved),
        }
      }

      case 'MOVE_RIGHT': {
        if (state.status !== 'playing' || !state.activePiece) return state
        const moved = move(state.board, state.activePiece, 1, 0)
        if (!moved) return state
        return {
          ...state,
          activePiece: moved,
          ghostPiece: calculateGhostPiece(state.board, moved),
        }
      }

      case 'MOVE': {
        if (state.status !== 'playing' || !state.activePiece) return state
        const moved = move(state.board, state.activePiece, action.dx, action.dy ?? 0)
        if (!moved) return state
        return {
          ...state,
          activePiece: moved,
          ghostPiece: calculateGhostPiece(state.board, moved),
        }
      }

      case 'ROTATE':
      case 'ROTATE_CW':
      case 'ROTATE_CCW': {
        if (state.status !== 'playing' || !state.activePiece) return state
        const direction = action.type === 'ROTATE_CCW'
          ? -1
          : (action.type === 'ROTATE' && action.direction ? action.direction : 1)
        const rotated = rotate(state.board, state.activePiece, direction)
        if (!rotated) return state
        return {
          ...state,
          activePiece: rotated,
          ghostPiece: calculateGhostPiece(state.board, rotated),
        }
      }

      case 'SOFT_DROP': {
        if (state.status !== 'playing' || !state.activePiece) return state
        const moved = move(state.board, state.activePiece, 0, 1)
        if (moved) {
          const nextScore = state.score + SOFT_DROP_POINT_PER_CELL
          return {
            ...state,
            activePiece: moved,
            ghostPiece: calculateGhostPiece(state.board, moved),
            score: nextScore,
            highScore: Math.max(state.highScore, nextScore),
          }
        }
        return state
      }

      case 'HARD_DROP': {
        if (state.status !== 'playing' || !state.activePiece) return state
        const droppedPiece = calculateGhostPiece(state.board, state.activePiece)
        const hardDropDistance = Math.max(0, droppedPiece.y - state.activePiece.y)
        return lockActivePiece(state, droppedPiece, hardDropDistance, 0, randomFn, minCapacity)
      }

      case 'LOCK': {
        if (state.status !== 'playing' || !state.activePiece) return state
        return lockActivePiece(state, state.activePiece, 0, 0, randomFn, minCapacity)
      }

      case 'HOLD': {
        if (state.status !== 'playing' || !state.activePiece || !state.canHold) return state
        const holdResult = holdPiece({
          activePiece: state.activePiece,
          heldPiece: state.heldPiece,
          canHold: state.canHold,
          queue: state.queue,
          minCapacity,
          randomFn,
        })
        if (!holdResult.swapped) return state

        if (!canPlace(state.board, holdResult.activePiece)) {
          return {
            ...state,
            status: 'game-over',
            activePiece: null,
            ghostPiece: null,
            heldPiece: holdResult.heldPiece,
            canHold: false,
            queue: holdResult.queue,
          }
        }

        return {
          ...state,
          activePiece: holdResult.activePiece,
          ghostPiece: calculateGhostPiece(state.board, holdResult.activePiece),
          heldPiece: holdResult.heldPiece,
          canHold: false,
          queue: holdResult.queue,
        }
      }

      case 'TICK': {
        if (state.status !== 'playing' || !state.activePiece) return state
        const delta = action.deltaMs ?? state.dropInterval
        const moved = move(state.board, state.activePiece, 0, 1)
        if (moved) {
          return {
            ...state,
            activePiece: moved,
            ghostPiece: calculateGhostPiece(state.board, moved),
            elapsedMs: state.elapsedMs + delta,
          }
        }
        const lockedState = lockActivePiece(state, state.activePiece, 0, 0, randomFn, minCapacity)
        return {
          ...lockedState,
          elapsedMs: state.elapsedMs + delta,
        }
      }

      case 'SET_HIGH_SCORE': {
        const nextHigh = Math.max(state.highScore, action.highScore)
        saveHighScore(nextHigh, state.mode)
        return {
          ...state,
          highScore: nextHigh,
        }
      }

      default:
        return state
    }
  }
}

export const tetrisGameReducer = createTetrisGameReducer()

export const tetrisActions = {
  start: (mode?: GameMode): GameAction => ({ type: 'START', mode }),
  pause: (): GameAction => ({ type: 'PAUSE' }),
  resume: (): GameAction => ({ type: 'RESUME' }),
  togglePause: (): GameAction => ({ type: 'TOGGLE_PAUSE' }),
  restart: (mode?: GameMode): GameAction => ({ type: 'RESTART', mode }),
  reset: (mode?: GameMode): GameAction => ({ type: 'RESET', mode }),
  moveLeft: (): GameAction => ({ type: 'MOVE_LEFT' }),
  moveRight: (): GameAction => ({ type: 'MOVE_RIGHT' }),
  move: (dx: number, dy: number = 0): GameAction => ({ type: 'MOVE', dx, dy }),
  rotate: (direction: 1 | -1 = 1): GameAction => ({ type: 'ROTATE', direction }),
  rotateCW: (): GameAction => ({ type: 'ROTATE_CW' }),
  rotateCCW: (): GameAction => ({ type: 'ROTATE_CCW' }),
  softDrop: (): GameAction => ({ type: 'SOFT_DROP' }),
  hardDrop: (): GameAction => ({ type: 'HARD_DROP' }),
  hold: (): GameAction => ({ type: 'HOLD' }),
  tick: (deltaMs?: number): GameAction => ({ type: 'TICK', deltaMs }),
  lock: (): GameAction => ({ type: 'LOCK' }),
  setHighScore: (highScore: number): GameAction => ({ type: 'SET_HIGH_SCORE', highScore }),
}

export interface TetrisActionController {
  getState: () => GameState
  dispatch: (action: GameAction) => GameState
  start: (mode?: GameMode) => GameState
  pause: () => GameState
  resume: () => GameState
  togglePause: () => GameState
  restart: (mode?: GameMode) => GameState
  reset: (mode?: GameMode) => GameState
  moveLeft: () => GameState
  moveRight: () => GameState
  move: (dx: number, dy?: number) => GameState
  rotate: (direction?: 1 | -1) => GameState
  rotateCW: () => GameState
  rotateCCW: () => GameState
  softDrop: () => GameState
  hardDrop: () => GameState
  hold: () => GameState
  tick: (deltaMs?: number) => GameState
  lock: () => GameState
  setHighScore: (highScore: number) => GameState
  subscribe: (listener: (state: GameState) => void) => () => void
}

export function createTetrisActionController(
  initialStateOrOptions?: GameState | TetrisGameOptions,
  customReducer?: (state: GameState, action: GameAction) => GameState
): TetrisActionController {
  const isState = initialStateOrOptions && 'board' in initialStateOrOptions && 'status' in initialStateOrOptions
  const options: TetrisGameOptions = isState ? {} : ((initialStateOrOptions as TetrisGameOptions) ?? {})
  const reducer = customReducer ?? (isState ? tetrisGameReducer : createTetrisGameReducer(options))

  let currentState: GameState = isState
    ? (initialStateOrOptions as GameState)
    : createInitialGameState(options)

  const listeners = new Set<(state: GameState) => void>()

  function dispatch(action: GameAction): GameState {
    const nextState = reducer(currentState, action)
    if (nextState !== currentState) {
      currentState = nextState
      listeners.forEach((listener) => {
        try {
          listener(currentState)
        } catch {
          // ignore listener errors
        }
      })
    }
    return currentState
  }

  return {
    getState: () => currentState,
    dispatch,
    start: (mode) => dispatch(tetrisActions.start(mode)),
    pause: () => dispatch(tetrisActions.pause()),
    resume: () => dispatch(tetrisActions.resume()),
    togglePause: () => dispatch(tetrisActions.togglePause()),
    restart: (mode) => dispatch(tetrisActions.restart(mode)),
    reset: (mode) => dispatch(tetrisActions.reset(mode)),
    moveLeft: () => dispatch(tetrisActions.moveLeft()),
    moveRight: () => dispatch(tetrisActions.moveRight()),
    move: (dx, dy) => dispatch(tetrisActions.move(dx, dy)),
    rotate: (direction) => dispatch(tetrisActions.rotate(direction)),
    rotateCW: () => dispatch(tetrisActions.rotateCW()),
    rotateCCW: () => dispatch(tetrisActions.rotateCCW()),
    softDrop: () => dispatch(tetrisActions.softDrop()),
    hardDrop: () => dispatch(tetrisActions.hardDrop()),
    hold: () => dispatch(tetrisActions.hold()),
    tick: (deltaMs) => dispatch(tetrisActions.tick(deltaMs)),
    lock: () => dispatch(tetrisActions.lock()),
    setHighScore: (highScore) => dispatch(tetrisActions.setHighScore(highScore)),
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}



